/**
 * Google Place → the address fields the onboarding forms save.
 *
 * The office/practice "basic information" step used to ask for country,
 * province, city and postal code by hand AND offer a Google address search that
 * filled in nothing but lat/long — the selected place's `address_components`
 * were thrown away. This is the one mapping from a Place to those fields, so the
 * three forms (UpdateOffice / UpdateOfficePractice / UpdatePractice) can't drift.
 *
 * Choices worth knowing:
 *  - `street` is `street_number` + `route` (what the address column wants); the
 *    full `formatted_address` is returned separately for callers that need a
 *    fallback when Google returns a business with no street line.
 *  - City precedence is explicit: `locality` first, then sublocalities, then
 *    `administrative_area_level_2`. (An older extractor in
 *    AudienceLocationFilter let a later component overwrite an earlier one, so a
 *    city-level result ended up in the province field.)
 *  - `country` is normalised to the two labels the forms' Select accepts —
 *    Google says "United States", the form says "United States of America". An
 *    unknown country comes back as "" so the Select's validator prompts.
 *  - lat/long are rounded to 7 decimals (≈1 cm), which fits the backend's
 *    Decimal columns; the forms used to truncate the string to 10 characters.
 *  - `country_code` / `state_code` are the ISO short names ("CA", "ON"). The
 *    onboarding forms want the spelled-out country, but the PAYMENT side wants
 *    the code: `BillingInformation.country` is a two-character column, so
 *    "Canada" cannot be stored there at all, and the rows already written hold
 *    "CA" / "ON". Both shapes are returned so neither caller has to convert.
 */
const COUNTRY_BY_CODE = { CA: "Canada", US: "United States of America" };
const COUNTRY_BY_NAME = {
  canada: "Canada",
  "united states": "United States of America",
  "united states of america": "United States of America",
};

function pick(components, type, field) {
  const hit = components.find(function (c) {
    return (c.types || []).indexOf(type) !== -1;
  });
  return hit ? String(hit[field || "long_name"] || "") : "";
}

function round7(n) {
  const v = Number(n);
  return Number.isFinite(v) ? Number(v.toFixed(7)) : null;
}

export function placeToAddress(place) {
  const components = (place && place.address_components) || [];
  const street = [pick(components, "street_number"), pick(components, "route")]
    .filter(Boolean)
    .join(" ");
  const city =
    pick(components, "locality") ||
    pick(components, "sublocality_level_1") ||
    pick(components, "sublocality") ||
    pick(components, "administrative_area_level_2");
  const state = pick(components, "administrative_area_level_1");
  const postal_code = pick(components, "postal_code");
  const code = pick(components, "country", "short_name").toUpperCase();
  const name = pick(components, "country").toLowerCase();
  const country = COUNTRY_BY_CODE[code] || COUNTRY_BY_NAME[name] || "";

  let latitude = null;
  let longitude = null;
  const loc = place && place.geometry && place.geometry.location;
  if (loc) {
    latitude = round7(typeof loc.lat === "function" ? loc.lat() : loc.lat);
    longitude = round7(typeof loc.lng === "function" ? loc.lng() : loc.lng);
  }

  return {
    street,
    formatted_address: (place && (place.formatted_address || place.name)) || "",
    city,
    state,
    state_code: pick(components, "administrative_area_level_1", "short_name"),
    postal_code,
    country,
    country_code: code,
    latitude,
    longitude,
  };
}

/** The line to put in an `address` field: the street, else the whole formatted
 * address, capped to the column length when one is given. */
export function addressLine(address, maxLength) {
  const line = (address && (address.street || address.formatted_address)) || "";
  return maxLength ? line.slice(0, maxLength) : line;
}
